// src/pages/Login.jsx
import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEnvelope, FaLock } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import styles from "./Login.module.css"; // ✨ Import styles object

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" }); // type will be "Info", "Success", or "Error"
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();

  const getRoleRoute = (role) => {
    if (role === 'superadmin' || role === 'admin') return '/dashboard/manage';
    return '/dashboard/my-portal';
  };

  /* Redirect destination: honour shared meeting links, fall back to role dashboard */
  const redirectTarget = (role) => location.state?.from || getRoleRoute(role);

  useEffect(() => {
    if (userData) {
      setMessage({ text: "Already logged in. Redirecting...", type: "Info" });
      navigate(redirectTarget(userData.role), { replace: true });
    }
  }, [userData, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: "Checking credentials...", type: "Info" });

    try {
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const docRef = doc(db, "users", userCred.user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setMessage({ text: "Account data not found. Contact support.", type: "Error" });
        await auth.signOut();
        setLoading(false);
        return;
      }
      
      setMessage({ text: "Login successful! Redirecting...", type: "Success" });
      const data = docSnap.data();

      setTimeout(() => {
        navigate(redirectTarget(data.role));
      }, 1000);

    } catch (error) {
      setMessage({ text: "Invalid email or password.", type: "Error" });
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!email) {
      return setMessage({ text: "Please enter email to reset.", type: "Error" });
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage({ text: "Reset link sent!", type: "Success" });
    } catch (error) {
      setMessage({ text: "Error sending reset link.", type: "Error" });
    } finally {
      setLoading(false);
    }
  };

  if (userData) {
    return (
      <div className={styles.loginPageContainer}>
        <div className={styles.loginFormPanel} style={{ justifyContent: 'center', alignItems: 'center' }}>
          <p className={`${styles.message} ${styles.messageInfo}`}>
            Already logged in. Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.loginPageContainer}>
      <div className={styles.loginImagePanel} />

      <div className={styles.loginFormPanel}>
        <div className={styles.loginFormWrapper}>
          <div className={styles.loginLogo}>
            <img 
              src="https://firebasestorage.googleapis.com/v0/b/employee-prepxpathshala.firebasestorage.app/o/profilePics%2FPrrepX%20Logo.jpg?alt=media&token=26c3e4ac-ecfd-4830-af53-f3213acb12a1" 
              alt="Logo" 
            />
          </div>
          <h2>Employee Portal</h2>
          <p className={styles.loginFormSubtitle}>Only for Employees/Admins of PrrepX Pathshala</p>

          <form onSubmit={handleLogin} className={styles.loginForm}>
            <div className={styles.inputGroup}>
              <FaEnvelope className={styles.inputIcon} />
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className={styles.inputGroup}>
              <FaLock className={styles.inputIcon} />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            {message.text && (
              <p className={`${styles.message} ${styles[`message${message.type}`]}`}>
                {message.text}
              </p>
            )}

            <button type="submit" className={styles.loginButton} disabled={loading}>
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>

          <button className={styles.forgotPasswordButton} onClick={handleReset} disabled={loading}>
            Forgot Password?
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;